import httpService from "./httpService";

// -- Types aligned to OpenAPI spec: advisor-client-api.openapi.json --

export interface ProductWeight {
  entityType: "PORTFOLIO" | "BASKET";
  productId: number;
  weight: number;
  position: "LONG" | "SHORT";
}

export interface AdvisorGroupSummary {
  id: number;
  name: string;
  memberCount: number;
  hasStrategy: boolean;
  createdAtUtc: string;
}

export interface AdvisorGroupMember {
  userId: string;
  name: string | null;
  email: string;
}

export interface AdvisorGroupDetail {
  id: number;
  name: string;
  strategy: ProductWeight[] | null;
  members: AdvisorGroupMember[];
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface MemberSyncOutcome {
  userId: string;
  success: boolean;
  error?: string;
  syncResult?: {
    created: number;
    updated: number;
    removed: number;
    subscriptionsModified: boolean;
  };
}

export interface GroupStrategySyncResult {
  groupId: number;
  totalMembers: number;
  succeeded: number;
  failed: number;
  memberOutcomes: MemberSyncOutcome[];
}

export interface UpdateGroupResponse {
  data: AdvisorGroupDetail;
  memberSyncOutcomes: MemberSyncOutcome[];
}

export interface CreateGroupRequest {
  name: string;
  memberUserIds?: string[];
}

export interface UpdateGroupRequest {
  name?: string;
  memberUserIds?: string[];
}

export interface SyncStrategiesRequest {
  strategies: ProductWeight[];
}

const advisorGroupService = {
  // -- Advisor self-service endpoints (ADVISOR role JWT) --

  listGroups: async (): Promise<AdvisorGroupSummary[]> => {
    const response = await httpService.get<{ data: AdvisorGroupSummary[] }>(
      "/v3/advisor/groups"
    );
    return response.data.data;
  },

  getGroup: async (groupId: number): Promise<AdvisorGroupDetail> => {
    const response = await httpService.get<{ data: AdvisorGroupDetail }>(
      `/v3/advisor/groups/${groupId}`
    );
    return response.data.data;
  },

  createGroup: async (payload: CreateGroupRequest): Promise<AdvisorGroupDetail> => {
    const response = await httpService.post<{ data: AdvisorGroupDetail }>(
      "/v3/advisor/groups",
      payload
    );
    return response.data.data;
  },

  updateGroup: async (groupId: number, payload: UpdateGroupRequest): Promise<UpdateGroupResponse> => {
    const response = await httpService.put<UpdateGroupResponse>(
      `/v3/advisor/groups/${groupId}`,
      payload
    );
    return response.data;
  },

  deleteGroup: async (groupId: number): Promise<void> => {
    await httpService.delete(`/v3/advisor/groups/${groupId}`);
  },

  updateStrategy: async (
    groupId: number,
    payload: SyncStrategiesRequest
  ): Promise<GroupStrategySyncResult> => {
    const response = await httpService.put<{ data: GroupStrategySyncResult }>(
      `/v3/advisor/groups/${groupId}/strategy`,
      payload
    );
    return response.data.data;
  },

  // -- Admin endpoints (ADMIN role, scoped by advisorAccountId) --

  listGroupsAdmin: async (advisorAccountId: number): Promise<AdvisorGroupSummary[]> => {
    const response = await httpService.get<{ data: AdvisorGroupSummary[] }>(
      `/v3/advisor/accounts/${advisorAccountId}/groups`
    );
    return response.data.data;
  },

  getGroupAdmin: async (advisorAccountId: number, groupId: number): Promise<AdvisorGroupDetail> => {
    const response = await httpService.get<{ data: AdvisorGroupDetail }>(
      `/v3/advisor/accounts/${advisorAccountId}/groups/${groupId}`
    );
    return response.data.data;
  },

  createGroupAdmin: async (advisorAccountId: number, payload: CreateGroupRequest): Promise<AdvisorGroupDetail> => {
    const response = await httpService.post<{ data: AdvisorGroupDetail }>(
      `/v3/advisor/accounts/${advisorAccountId}/groups`,
      payload
    );
    return response.data.data;
  },

  updateGroupAdmin: async (advisorAccountId: number, groupId: number, payload: UpdateGroupRequest): Promise<UpdateGroupResponse> => {
    const response = await httpService.put<UpdateGroupResponse>(
      `/v3/advisor/accounts/${advisorAccountId}/groups/${groupId}`,
      payload
    );
    return response.data;
  },

  deleteGroupAdmin: async (advisorAccountId: number, groupId: number): Promise<void> => {
    await httpService.delete(`/v3/advisor/accounts/${advisorAccountId}/groups/${groupId}`);
  },

  updateStrategyAdmin: async (
    advisorAccountId: number,
    groupId: number,
    payload: SyncStrategiesRequest
  ): Promise<GroupStrategySyncResult> => {
    const response = await httpService.put<{ data: GroupStrategySyncResult }>(
      `/v3/advisor/accounts/${advisorAccountId}/groups/${groupId}/strategy`,
      payload
    );
    return response.data.data;
  },
};

export default advisorGroupService;
